/**
 * Opener-independent fallback channel for delivering the OAuth handoff payload from the
 * embedded-menu SSO popup (`/oauth/google`, `/oauth/social`) back to the embedded-menu iframe
 * that opened it (`src/hooks/use-embedded-events.js`).
 *
 * The primary channel is `window.opener.postMessage(...)`, restricted to the redirect URL's own
 * origin. But `window.opener` can be `null` by the time the OAuth provider redirects back here --
 * e.g. some providers set a strict `Cross-Origin-Opener-Policy` on their own auth pages, which
 * permanently severs the popup's browsing-context-group link to its opener, even once the popup
 * later navigates back to a same-origin `/oauth/*` callback page. When that happens, `postMessage`
 * has no live target at all.
 *
 * A `localStorage` write fires a `storage` event in every *other* window/tab that shares this
 * page's origin (never in the window that wrote it), independent of any `window.opener`
 * reference -- so it still reaches the iframe when the opener link is gone.
 *
 * Residual risk (does not fully close, see PR discussion / SCOPE notes):
 * - This is strictly same-origin. If the OAuth callback route and the embedded-menu iframe are
 *   ever served from different origins, this channel cannot cross that boundary -- only the
 *   origin-restricted `postMessage` can, and only while `window.opener` is alive.
 * - Some browsers partition storage for third-party/embedded contexts (Safari ITP, Chrome CHIPS
 *   in some configurations). If the popup's top-level browsing context and the iframe's ended up
 *   in different storage partitions, neither the `storage` event nor a direct read-back would
 *   ever observe this write. That degrades to "no fallback available," not to anything worse than
 *   the pre-fix behavior.
 */

export const EMBEDDED_OAUTH_CHANNEL_KEY = 'dutchie:embeddedOAuthHandoff';

export type EmbeddedOAuthHandoffPayload = {
  event: 'embed:oAuthLogin';
  token: string;
  isNewUser: boolean;
};

/**
 * Writes the handoff payload to the shared, dedicated storage key. Never throws -- storage
 * access can fail (private browsing quota, disabled storage, partitioned contexts), and a
 * failure here must never prevent the popup from closing.
 */
export function writeEmbeddedOAuthHandoff(payload: EmbeddedOAuthHandoffPayload): void {
  try {
    window.localStorage.setItem(EMBEDDED_OAUTH_CHANNEL_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error(error);
  }
}

/** Removes the handoff key. Best-effort; never throws. */
export function clearEmbeddedOAuthHandoff(): void {
  try {
    window.localStorage.removeItem(EMBEDDED_OAUTH_CHANNEL_KEY);
  } catch {
    // best-effort cleanup only
  }
}

/**
 * Extracts a handoff payload from a `storage` event, if it's one this channel wrote. Returns
 * `null` for any other storage event (including this key being *cleared*, where `newValue` is
 * `null`), or if the value fails to parse as the expected payload shape.
 */
export function readEmbeddedOAuthHandoff(
  event: Pick<StorageEvent, 'key' | 'newValue'>
): EmbeddedOAuthHandoffPayload | null {
  if (event.key !== EMBEDDED_OAUTH_CHANNEL_KEY || !event.newValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(event.newValue) as Partial<EmbeddedOAuthHandoffPayload>;

    if (parsed.event !== 'embed:oAuthLogin' || typeof parsed.token !== 'string') {
      return null;
    }

    return { event: 'embed:oAuthLogin', token: parsed.token, isNewUser: Boolean(parsed.isNewUser) };
  } catch {
    return null;
  }
}
